import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Activity, CheckCircle, XCircle, AlertTriangle, RefreshCw } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function WebhookHealthPage() {
  const { adminKey } = useOutletContext();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);

  useEffect(() => {
    fetchHealth();
    
    if (autoRefresh) {
      const interval = setInterval(fetchHealth, 10000);
      return () => clearInterval(interval);
    }
  }, [adminKey, autoRefresh]);

  const fetchHealth = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/webhook-health`, {
        headers: { "X-Admin-Key": adminKey }
      });
      
      if (res.ok) {
        setHealth(await res.json());
      }
    } catch (err) {
      console.error("Failed to fetch health:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
      </div>
    );
  }

  const isHealthy = health?.status === "healthy";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Webhook Health</h1>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-gray-600 text-sm">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-gray-300 bg-gray-50"
            />
            Auto-refresh (10s)
          </label>
          <button 
            onClick={fetchHealth}
            className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-800 rounded-lg hover:bg-gray-200"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Main Status Banner */}
      <div className={`rounded-xl p-6 border ${isHealthy ? "bg-emerald-900/30 border-green-200" : "bg-red-900/30 border-red-200"}`}>
        <div className="flex items-center gap-4">
          {isHealthy ? (
            <CheckCircle className="w-12 h-12 text-green-600" />
          ) : (
            <AlertTriangle className="w-12 h-12 text-red-600" />
          )}
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              System Status: {health?.status?.toUpperCase()}
            </h2>
            <p className="text-gray-600">
              Last updated: {new Date(health?.timestamp).toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard 
          title="Total Events (1hr)" 
          value={health?.last_hour?.total_events || 0}
          icon={Activity}
          color="blue"
        />
        <StatCard 
          title="Processed" 
          value={health?.last_hour?.processed || 0}
          icon={CheckCircle}
          color="green"
        />
        <StatCard 
          title="Failed (1hr)" 
          value={health?.last_hour?.failed || 0}
          icon={XCircle}
          color="red"
        />
        <StatCard 
          title="Total Quarantined" 
          value={health?.quarantined_total || 0}
          icon={AlertTriangle}
          color="orange"
        />
      </div>

      {/* Recent Quarantined Events */}
      {health?.recent_quarantined?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-gray-800 font-semibold">Recent Quarantined Events</h2>
            <span className="px-3 py-1 rounded-full bg-orange-600 text-gray-800 text-xs font-bold">
              {health.recent_quarantined.length}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Event ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Error</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {health.recent_quarantined.map((event, idx) => (
                  <tr key={event.id || idx} className="hover:bg-gray-50/30">
                    <td className="px-4 py-3 text-gray-800 font-mono text-sm">{event.id}</td>
                    <td className="px-4 py-3 text-gray-600">{event.type}</td>
                    <td className="px-4 py-3 text-red-600 text-sm max-w-md truncate">{event.error}</td>
                    <td className="px-4 py-3 text-gray-500 text-sm">
                      {new Date(event.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Health Indicators */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-gray-800 font-semibold mb-4">Health Indicators</h2>
        <div className="space-y-3">
          <HealthIndicator 
            label="Webhook Processing" 
            status={health?.last_hour?.failed === 0 ? "healthy" : "degraded"}
            detail={`${health?.last_hour?.processed || 0} processed, ${health?.last_hour?.failed || 0} failed in last hour`}
          />
          <HealthIndicator 
            label="Quarantine Queue" 
            status={health?.quarantined_total === 0 ? "healthy" : health?.quarantined_total < 10 ? "warning" : "critical"}
            detail={`${health?.quarantined_total || 0} events in quarantine`}
          />
          <HealthIndicator 
            label="Event Volume" 
            status={health?.last_hour?.total_events > 0 ? "healthy" : "idle"}
            detail={`${health?.last_hour?.total_events || 0} events in last hour`}
          />
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color }) {
  const colors = {
    blue: "from-blue-600 to-blue-700",
    green: "from-emerald-600 to-emerald-700",
    red: "from-red-600 to-red-700",
    orange: "from-orange-600 to-orange-700",
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500 text-sm">{title}</p>
          <p className="text-3xl font-bold text-gray-800 mt-1">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colors[color]} flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-gray-800" />
        </div>
      </div>
    </div>
  );
}

function HealthIndicator({ label, status, detail }) {
  const getStatusStyle = () => {
    switch (status) {
      case "healthy": return { bg: "bg-emerald-500", text: "text-green-600" };
      case "warning": return { bg: "bg-amber-500", text: "text-yellow-700" };
      case "degraded": return { bg: "bg-orange-500", text: "text-orange-400" };
      case "critical": return { bg: "bg-red-500", text: "text-red-600" };
      case "idle": return { bg: "bg-slate-500", text: "text-gray-500" };
      default: return { bg: "bg-slate-500", text: "text-gray-500" };
    }
  };

  const style = getStatusStyle();

  return (
    <div className="flex items-center gap-4 p-3 bg-gray-50/50 rounded-lg">
      <div className={`w-3 h-3 rounded-full ${style.bg}`} />
      <div className="flex-1">
        <p className="text-gray-800 font-medium">{label}</p>
        <p className="text-gray-500 text-sm">{detail}</p>
      </div>
      <span className={`text-sm font-semibold capitalize ${style.text}`}>{status}</span>
    </div>
  );
}

export default WebhookHealthPage;
